<template>
  <nav class="bg-white border-b border-gray-200">
    <div class="container-lg">
      <div class="flex justify-between h-16">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <router-link to="/" class="text-primary text-xl font-bold"
              >Crescendo</router-link
            >
          </div>
          <div class="hidden sm:ml-8 sm:flex sm:items-center">
            <router-link
              to="/"
              class="nav-link"
              :class="{ 'nav-link-active': $route.path === '/' }"
            >
              Home
            </router-link>
            <router-link
              to="/teachers"
              class="nav-link"
              :class="{ 'nav-link-active': $route.path.includes('/teachers') }"
              data-testid="nav-teachers"
            >
              Find Teachers
            </router-link>
            <router-link
              v-if="FeatureFlags.core.groupLessons"
              to="/group-lessons"
              class="nav-link"
              :class="{
                'nav-link-active': $route.path.includes('/group-lessons'),
              }"
              data-testid="nav-group-lessons"
            >
              Find Group Lessons
            </router-link>
          </div>
        </div>
        <div class="hidden sm:flex sm:items-center">
          <template v-if="user">
            <div class="flex items-center">
              <router-link
                to="/dashboard"
                class="nav-link mr-2"
                :class="{ 'nav-link-active': $route.path === '/dashboard' }"
                data-testid="dashboard-nav"
              >
                Dashboard
              </router-link>

              <router-link
                to="/messages"
                class="nav-link mr-2 relative"
                :class="{
                  'nav-link-active': $route.path.startsWith('/messages'),
                }"
                data-testid="messages-nav"
              >
                Messages
                <!-- Unread count badge -->
                <span
                  v-if="unreadCount > 0"
                  class="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-5 h-5"
                >
                  {{ unreadCount > 99 ? "99+" : unreadCount }}
                </span>
              </router-link>

              <!-- User dropdown menu -->
              <div class="relative ml-3" ref="userMenuRef">
                <button
                  @click="showUserMenu = !showUserMenu"
                  class="flex items-center text-sm rounded-full bg-gray-100 hover:bg-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  data-testid="user-menu"
                >
                  <span
                    class="font-medium text-gray-700"
                    data-testid="user-name"
                  >
                    {{
                      user.displayName || user.email?.split("@")[0] || "User"
                    }}
                  </span>
                  <svg
                    class="ml-2 h-4 w-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                <!-- Dropdown menu -->
                <div
                  v-if="showUserMenu"
                  class="origin-top-right absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
                >
                  <div class="py-2">
                    <div class="px-4 py-2 border-b border-gray-100">
                      <p
                        class="text-sm font-medium text-gray-900"
                        data-testid="user-name"
                      >
                        {{ user.displayName || "User" }}
                      </p>
                      <p class="text-sm text-gray-600" data-testid="user-email">
                        {{ user.email }}
                      </p>
                      <p
                        class="text-xs text-gray-500 mt-1"
                        data-testid="user-type"
                      >
                        {{ getUserType() }}
                      </p>
                    </div>
                    <button
                      @click="logout"
                      class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      data-testid="logout-button"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <router-link to="/login" class="nav-link" data-testid="nav-login">
              Login
            </router-link>
            <router-link
              to="/register"
              class="btn-primary ml-4"
              data-testid="nav-register"
            >
              Register
            </router-link>
          </template>
        </div>
        <!-- Mobile menu button -->
        <div class="flex items-center sm:hidden">
          <button
            @click="isOpen = !isOpen"
            class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none"
            aria-expanded="false"
          >
            <span class="sr-only">Open main menu</span>
            <svg
              class="block h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <div
      :class="{ block: isOpen, hidden: !isOpen }"
      class="sm:hidden border-t border-gray-200"
    >
      <div class="pt-2 pb-3 space-y-1">
        <router-link
          to="/"
          class="nav-link block px-4"
          :class="{ 'nav-link-active': $route.path === '/' }"
          @click="isOpen = false"
        >
          Home
        </router-link>
        <router-link
          to="/teachers"
          class="nav-link block px-4"
          :class="{ 'nav-link-active': $route.path.includes('/teachers') }"
          @click="isOpen = false"
          data-testid="nav-teachers-mobile"
        >
          Find Teachers
        </router-link>
        <router-link
          v-if="FeatureFlags.core.groupLessons"
          to="/group-lessons"
          class="nav-link block px-4"
          :class="{ 'nav-link-active': $route.path.includes('/group-lessons') }"
          @click="isOpen = false"
          data-testid="nav-group-lessons-mobile"
        >
          Find Group Lessons
        </router-link>
        <template v-if="user">
          <router-link
            to="/dashboard"
            class="nav-link block px-4"
            :class="{ 'nav-link-active': $route.path === '/dashboard' }"
            @click="isOpen = false"
          >
            Dashboard
          </router-link>
          <router-link
            to="/messages"
            class="nav-link block px-4 relative"
            :class="{ 'nav-link-active': $route.path.startsWith('/messages') }"
            @click="isOpen = false"
          >
            Messages
            <!-- Unread count badge for mobile -->
            <span
              v-if="unreadCount > 0"
              class="absolute top-1/2 right-4 transform -translate-y-1/2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-5 h-5"
            >
              {{ unreadCount > 99 ? "99+" : unreadCount }}
            </span>
          </router-link>
          <a href="#" @click.prevent="logout" class="nav-link block px-4">
            Logout
          </a>
        </template>
        <template v-else>
          <router-link
            to="/login"
            class="nav-link block px-4"
            :class="{ 'nav-link-active': $route.path === '/login' }"
            @click="isOpen = false"
            data-testid="nav-login-mobile"
          >
            Login
          </router-link>
          <router-link
            to="/register"
            class="nav-link block px-4"
            :class="{ 'nav-link-active': $route.path === '/register' }"
            @click="isOpen = false"
            data-testid="nav-register-mobile"
          >
            Register
          </router-link>
        </template>
      </div>
    </div>
  </nav>
</template>

<script>
import { ref, computed, watch, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { logoutUser, getCurrentUser } from "@/services/auth.service";
import { getTotalUnreadCount } from "@/services/messaging.service";
import { FeatureFlags } from "@/config/features";
import { useClickOutside } from "@/composables/useClickOutside";

export default {
  name: "NavBar",
  setup() {
    const isOpen = ref(false);
    const showUserMenu = ref(false);
    const userMenuRef = ref(null);
    const router = useRouter();
    const user = computed(() => getCurrentUser());
    const unreadCount = ref(0);

    useClickOutside(userMenuRef, () => {
      showUserMenu.value = false;
    });

    let unreadInterval = null;

    const logout = async () => {
      try {
        console.log("Logging out...");
        await logoutUser();
        console.log("Logged out successfully");

        router.push("/login");
      } catch (error) {
        console.error("Error logging out:", error);
      }
    };

    // Update unread count
    const updateUnreadCount = async () => {
      if (user.value) {
        try {
          const count = await getTotalUnreadCount(user.value.uid);
          unreadCount.value = count;
        } catch (error) {
          console.error("Error getting unread count:", error);
        }
      }
    };

    watch(
      user,
      (newUser, oldUser) => {
        if (newUser && !oldUser) {
          updateUnreadCount();
          unreadInterval = setInterval(updateUnreadCount, 30000);
        } else if (!newUser && oldUser) {
          unreadCount.value = 0;
          if (unreadInterval) {
            clearInterval(unreadInterval);
            unreadInterval = null;
          }
        }
      },
      { immediate: true }
    );

    onUnmounted(() => {
      if (unreadInterval) {
        clearInterval(unreadInterval);
      }
    });

    // Get user type for display
    const getUserType = () => {
      if (!user.value) return "";

      // Check for common user type properties
      if (user.value.isTeacher || user.value.userType === "teacher") {
        return "Teacher";
      } else if (user.value.isAdmin || user.value.userType === "admin") {
        return "Admin";
      } else {
        return "Student";
      }
    };

    // Check if user is a teacher
    const getUserIsTeacher = () => {
      if (!user.value) return false;
      return user.value.isTeacher || user.value.userType === "teacher";
    };

    return {
      isOpen,
      showUserMenu,
      userMenuRef,
      user,
      unreadCount,
      logout,
      getUserType,
      getUserIsTeacher,
      FeatureFlags,
    };
  },
};
</script>
